import { Link } from "react-router-dom";
import { padIndex } from "../../lib/format";
import { IconArrowUpRight } from "../Icons";

export type FeaturedIndexItem = {
	id: string;
	href: string;
	title: string;
	excerpt?: string;
	cover?: string;
	number: number;
	eyebrow?: string;
	aside?: string;
	dateTime?: string;
	cta: string;
};

type FeaturedIndexProps = {
	items: FeaturedIndexItem[];
	empty?: string;
	/** spotlight = lead card + list (blog). cards = every item uses the lead layout (lab). */
	layout?: "spotlight" | "cards";
	/** Home sections sit under an h2, so cards default to h3. Lab index is under h1. */
	heading?: "h2" | "h3";
};

function FeaturedAside({
	aside,
	dateTime,
}: {
	aside?: string;
	dateTime?: string;
}) {
	if (!aside) return null;
	if (dateTime) {
		return (
			<time className="featured-aside" dateTime={dateTime}>
				{aside}
			</time>
		);
	}
	return <span className="featured-aside">{aside}</span>;
}

function FeaturedCard({
	item,
	heading: Title,
}: {
	item: FeaturedIndexItem;
	heading: "h2" | "h3";
}) {
	return (
		<Link to={item.href} className="featured-lead">
			<div className="featured-lead-media">
				{item.cover ? (
					<img
						src={item.cover}
						alt={item.title}
						loading="lazy"
						decoding="async"
					/>
				) : null}
			</div>
			<div className="featured-lead-copy">
				<div className="featured-meta">
					{item.eyebrow ? <span>{item.eyebrow}</span> : null}
					<FeaturedAside aside={item.aside} dateTime={item.dateTime} />
				</div>
				<Title className="featured-lead-title">{item.title}</Title>
				{item.excerpt ? <p>{item.excerpt}</p> : null}
				<div className="featured-lead-footer">
					<span className="featured-cta">
						{item.cta} <IconArrowUpRight width="16" height="16" />
					</span>
					<span className="featured-lead-index">{padIndex(item.number)}</span>
				</div>
			</div>
		</Link>
	);
}

export default function FeaturedIndex({
	items,
	empty,
	layout = "spotlight",
	heading = "h3",
}: FeaturedIndexProps) {
	if (!items.length) {
		return empty ? <p className="mt-8">{empty}</p> : null;
	}

	if (layout === "cards") {
		return (
			<div className="featured-cards">
				{items.map((item) => (
					<FeaturedCard key={item.id} item={item} heading={heading} />
				))}
			</div>
		);
	}

	const lead = items[0];
	const rest = items.slice(1);

	return (
		<div className="featured-layout">
			<FeaturedCard item={lead} heading={heading} />
			<div className="featured-index">
				{rest.map((item) => (
					<Link key={item.id} to={item.href}>
						<span>{padIndex(item.number)}</span>
						<strong>{item.title}</strong>
						<FeaturedAside aside={item.aside} dateTime={item.dateTime} />
						<IconArrowUpRight width="16" height="16" />
					</Link>
				))}
			</div>
		</div>
	);
}
